<script>
  import { KeyRound, LockKeyhole } from 'lucide-svelte';
  import SettingsCard from '../shared/SettingsCard.svelte';

  export let form;
  export let onChangePin = () => {};
  export let onChangePassword = () => {};

  const bypassOptions = [1000, 2000, 3000, 5000];
  const buttonBase =
    'inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

  $: hasPin = !!(form?.hasPin ?? form?.pinEnabled ?? form?.pinenabled);
  $: pinLengthLabel = hasPin ? form.pinLength : 'Not set';
  $: bypassMilliseconds = normalizeBypassMs(form?.bypassMilliseconds);

  function normalizeBypassMs(value) {
    const next = Number(value);

    if (!Number.isFinite(next)) return 1000;

    return Math.min(60000, Math.max(500, Math.round(next)));
  }

  function updateBypass(value) {
    form.bypassMilliseconds = normalizeBypassMs(value);
  }
</script>

<SettingsCard title="Security" description="Password and PIN access controls.">
  <span
    slot="actions"
    class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold {hasPin
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-amber-50 text-amber-700'}">
    {hasPin ? 'PIN Enabled' : 'PIN Not Created'}
  </span>

  <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]">
    <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <dt class="text-xs font-medium uppercase text-slate-500">PIN Status</dt>
        <dd class="mt-1 text-sm font-semibold {hasPin ? 'text-emerald-700' : 'text-amber-700'}">
          {hasPin ? 'Enabled' : 'Not created'}
        </dd>
      </div>

      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <dt class="text-xs font-medium uppercase text-slate-500">PIN Length</dt>
        <dd class="mt-1 text-sm font-semibold text-slate-900">{pinLengthLabel}</dd>
      </div>

      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <dt class="text-xs font-medium uppercase text-slate-500">Max Attempts</dt>
        <dd class="mt-1 text-sm font-semibold text-slate-900">{form.maxAttempts}</dd>
      </div>

      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <dt class="text-xs font-medium uppercase text-slate-500">Lock Seconds</dt>
        <dd class="mt-1 text-sm font-semibold text-slate-900">{form.lockSeconds}</dd>
      </div>
    </dl>

    <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {#if !hasPin}
        <label for="security-bypass-ms" class="block text-sm font-medium text-slate-700">Bypass Milliseconds</label>
        <div class="mt-2 flex items-center gap-2">
          <input
            id="security-bypass-ms"
            type="number"
            min="500"
            max="60000"
            step="500"
            class="h-10 w-32 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            value={bypassMilliseconds}
            on:change={(event) => updateBypass(event.currentTarget.value)} />
          <span class="text-xs text-slate-500">ms</span>
        </div>

        <div class="mt-3 flex flex-wrap gap-2">
          {#each bypassOptions as option}
            <button
              type="button"
              class="rounded-lg border px-2.5 py-1 text-xs font-medium transition {bypassMilliseconds === option
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50'}"
              on:click={() => updateBypass(option)}>
              {option}ms
            </button>
          {/each}
        </div>
      {:else}
        <div class="flex items-start gap-3">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <LockKeyhole size={18} />
          </div>
          <div>
            <p class="text-sm font-semibold text-slate-900">PIN protection is active</p>
            <p class="mt-1 text-sm text-slate-500">Use the action below to rotate the current PIN.</p>
          </div>
        </div>
      {/if}
    </div>
  </div>

  <div class="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
    <button class={`${buttonBase} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`} on:click={onChangePassword}>
      <KeyRound size={16} />
      Change Password
    </button>

    <button
      class={`${buttonBase} ${hasPin
        ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
        : 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'}`}
      on:click={onChangePin}>
      <LockKeyhole size={16} />
      {hasPin ? 'Change PIN' : 'Create PIN'}
    </button>
  </div>
</SettingsCard>
